import * as React from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import Box from '@cloudscape-design/components/box';
import Header from '@cloudscape-design/components/header';
import Table from '@cloudscape-design/components/table';
import { formatCurrencyCents, formatUsdCents } from '@/features/dashboard-finance/formatters';
import type { CreatedTenantRow } from './useSalesMetrics';
import { TableFilters } from './TableFilters';

function formatDate(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10);
}

/**
 * Tabela das vendas diretas criadas no período, com o equivalente mensal na
 * moeda nativa e em USD.
 */
export function CreatedTenantsTable({
  rows,
}: {
  rows: readonly CreatedTenantRow[];
}): React.JSX.Element {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const country = params.get('createdCountry') ?? '';
  const currency = params.get('createdCurrency') ?? '';

  const filtered = useMemo(
    () =>
      rows.filter((r) => {
        if (country && r.countryCode !== country) return false;
        if (currency && r.currency !== currency) return false;
        return true;
      }),
    [rows, country, currency],
  );

  return (
    <Table<CreatedTenantRow>
      items={filtered}
      trackBy="tenantId"
      variant="container"
      header={
        <Header
          variant="h3"
          actions={<TableFilters rows={rows} countryParam="createdCountry" currencyParam="createdCurrency" />}
        >
          {t('sales.tables.created.title', { count: filtered.length })}
        </Header>
      }
      empty={<Box textAlign="center" color="inherit">{t('sales.empty')}</Box>}
      columnDefinitions={[
        { id: 'name', header: t('sales.tables.created.name'), cell: (r): React.ReactNode => r.tenantName },
        { id: 'country', header: t('sales.tables.created.country'), cell: (r): React.ReactNode => r.countryCode },
        { id: 'currency', header: t('sales.tables.created.currency'), cell: (r): React.ReactNode => r.currency },
        {
          id: 'price',
          header: t('sales.tables.created.price'),
          cell: (r): React.ReactNode => formatCurrencyCents(r.priceCents, r.currency),
        },
        {
          id: 'usdEq',
          header: t('sales.tables.created.usdEq'),
          cell: (r): React.ReactNode => formatUsdCents(r.usdEqCents),
        },
        {
          id: 'createdAt',
          header: t('sales.tables.created.createdAt'),
          cell: (r): React.ReactNode => formatDate(r.createdAt),
        },
      ]}
    />
  );
}
